import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSecuritySummary } from '../api/internal/usersInternalApi';
import {
    Card, CardTitle, PageHead, Pill, EmptyState,
    Spinner, SpinnerContainer, Notice, DataTable, TableScroll,
} from '../components/ui';
import {
    StatGrid, StatTile, SeverityBar, SeveritySegment, SeverityLegend, LegendDot,
    MonoCell, Muted, ClickableRow,
} from '../components/security/security.styled';
import {
    SeverityPill, SEVERITY_ORDER, SEVERITY_THEME_KEY, formatEpss,
} from '../components/security/severity';
import DetailConformite, { TuilePosture, usePostureHistorique } from '../components/security/complianceHistory';

export default function SecurityOverview() {
    const navigate = useNavigate();
    // L'historique est chargé ici pour que la tuile et son détail lisent la même
    // série : deux chargements donneraient deux chiffres pour un même périmètre.
    const historique = usePostureHistorique();
    // Quelle tuile a ouvert le détail : null, 'conformite' ou 'exposition'.
    // Un booléen partagé surlignerait les deux tuiles à la fois.
    const [detailOuvert, setDetailOuvert] = useState(null);
    const basculer = (cle) => setDetailOuvert((actuel) => (actuel === cle ? null : cle));
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const controller = new AbortController();
        getSecuritySummary(controller.signal)
            .then((data) => { if (data) setSummary(data); })
            .catch(() => setError("Impossible de charger la synthèse Defender."))
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, []);

    if (loading) {
        return (
            <Card>
                <SpinnerContainer><Spinner /></SpinnerContainer>
            </Card>
        );
    }

    if (error || !summary) {
        return (
            <>
                <PageHead>
                    <div><h2>Sécurité</h2></div>
                </PageHead>
                <Card>
                    <Notice $tone="danger">
                        <i className="bx bx-error-circle" />
                        {error || 'Aucune donnée disponible.'}
                    </Notice>
                </Card>
            </>
        );
    }

    const { cves, devices } = summary;
    const relevesHistorique = historique.donnees?.semaines || [];
    const dernierReleve = relevesHistorique[relevesHistorique.length - 1] || null;
    const totalCve = cves.total || 1; // évite une division par zéro sur la barre

    return (
        <>
            <PageHead>
                <div>
                    <h2>Sécurité</h2>
                    <p>
                        Exposition du parc aux vulnérabilités, d'après Microsoft Defender.
                        {summary.lastSync && (
                            <> Dernière synchronisation le {new Date(summary.lastSync).toLocaleString('fr-FR')}.</>
                        )}
                    </p>
                </div>
            </PageHead>

            <StatGrid>
                {/* Valeur lue en direct chez Defender, tendance issue des
                    instantanés quotidiens : la tuile reste à la minute tout en
                    montrant l'évolution. */}
                <TuilePosture
                    titre="Score d'exposition"
                    donnees={historique.donnees}
                    champ="exposureScore"
                    valeurCourante={summary.exposureScore}
                    formater={(v) => `${Math.round(v * 10) / 10}`}
                    sensFavorable={-1}
                    unite=" pts"
                    sousTitre="Plus bas est meilleur"
                    ouvert={detailOuvert === 'exposition'}
                    onBasculer={() => basculer('exposition')}
                />
                <StatTile $tone="accent">
                    <div className="label">Score de configuration</div>
                    <div className="value">
                        {summary.configurationScore === null ? '—' : Math.round(summary.configurationScore)}
                    </div>
                    <div className="hint">Plus haut est meilleur</div>
                </StatTile>
                {/* Conformité : même famille visuelle que les scores, mais
                    cliquable — la tendance se lit ici, la courbe derrière. */}
                <TuilePosture
                    titre="Conformité des postes"
                    donnees={historique.donnees}
                    champ="tauxSurEvalues"
                    formater={(v) => `${Math.round(v * 10) / 10} %`}
                    sensFavorable={1}
                    unite=" pts"
                    sousTitre={dernierReleve
                        ? `${dernierReleve.conformes} conformes sur ${dernierReleve.conformes + dernierReleve.nonConformes} évalués`
                        : 'aucun relevé'}
                    ouvert={detailOuvert === 'conformite'}
                    onBasculer={() => basculer('conformite')}
                />

                <StatTile $tone="danger">
                    <div className="label">Vulnérabilités</div>
                    <div className="value">{cves.total.toLocaleString('fr-FR')}</div>
                    <div className="hint">{cves.withExploit} avec exploit connu</div>
                </StatTile>
                <StatTile $tone="warn">
                    <div className="label">Postes exposés</div>
                    <div className="value">{devices.vulnerable.toLocaleString('fr-FR')}</div>
                    <div className="hint">sur {devices.total.toLocaleString('fr-FR')} appareils</div>
                </StatTile>
            </StatGrid>

            {/* Déplié au clic sur la tuile Conformité, jamais par défaut : la
                vue d'ensemble reste une photographie, la tendance est un détail
                qu'on demande. */}
            {detailOuvert && <DetailConformite {...historique} focus={detailOuvert} />}

            <Card>
                <CardTitle>Répartition par sévérité</CardTitle>
                <SeverityBar>
                    {SEVERITY_ORDER.map((severity) => {
                        const count = cves.bySeverity[severity] || 0;
                        if (count === 0) return null;
                        return (
                            <SeveritySegment
                                key={severity}
                                title={`${severity} : ${count}`}
                                $width={(count / totalCve) * 100}
                                $toneKey={SEVERITY_THEME_KEY[severity]}
                            />
                        );
                    })}
                </SeverityBar>
                <SeverityLegend>
                    {SEVERITY_ORDER.map((severity) => (
                        <span key={severity}>
                            <LegendDot $toneKey={SEVERITY_THEME_KEY[severity]} />
                            {severity} <strong>{(cves.bySeverity[severity] || 0).toLocaleString('fr-FR')}</strong>
                        </span>
                    ))}
                </SeverityLegend>
            </Card>

            <Card $pad="16px">
                <CardTitle>
                    À traiter en priorité
                    <Muted style={{ fontWeight: 400, fontSize: '0.78rem', marginLeft: '8px' }}>
                        critiques et hautes, triées par probabilité d'exploitation
                    </Muted>
                </CardTitle>

                {summary.topCves.length === 0 ? (
                    <EmptyState>
                        <i className="bx bx-shield-quarter" />
                        <h3>Aucune vulnérabilité prioritaire</h3>
                        <p>Aucune CVE critique ou haute n'est actuellement recensée.</p>
                    </EmptyState>
                ) : (
                    <TableScroll>
                        <DataTable>
                            <thead>
                                <tr>
                                    <th>CVE</th>
                                    <th>Sévérité</th>
                                    <th>CVSS</th>
                                    <th>EPSS</th>
                                    <th>Exploit</th>
                                    <th>Appareils</th>
                                    <th>Logiciel</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Ligne entière cliquable plutôt qu'un lien sur le
                                    code : même comportement et même couleur que la
                                    page Vulnérabilités, où le bleu de lien faisait
                                    tache dans un tableau. */}
                                {summary.topCves.map((cve) => (
                                    <ClickableRow
                                        key={cve.cveCode}
                                        onClick={() => navigate(`/Security/Vulnerabilities?cve=${cve.cveCode}`)}
                                    >
                                        <td><MonoCell>{cve.cveCode}</MonoCell></td>
                                        <td><SeverityPill severity={cve.severity} /></td>
                                        <td>{cve.cvssScore ?? '—'}</td>
                                        <td>{formatEpss(cve.epssScore)}</td>
                                        <td>
                                            {cve.exploitabilityLevel === 'PoC'
                                                ? <Pill $tone="danger">Disponible</Pill>
                                                : <Muted>—</Muted>}
                                        </td>
                                        <td>{cve.affectedCount}</td>
                                        <td><Muted>{(cve.software || []).join(', ') || '—'}</Muted></td>
                                    </ClickableRow>
                                ))}
                            </tbody>
                        </DataTable>
                    </TableScroll>
                )}
            </Card>
        </>
    );
}
